// Data transformation layer for site survey data
// Handles conversion between API snake_case and UI camelCase formats

import { z } from 'zod';

// Equipment options configuration
export const EQUIPMENT_OPTIONS = {
  panels: [
    { value: 'JA Solar 455W Mono', label: 'JA Solar 455W Mono' },
    { value: 'Eurener Nexa 500W', label: 'Eurener Nexa 500W' },
    { value: "Aiko's Neostar 2S 460W", label: "Aiko's Neostar 2S 460W" },
    { value: 'Aiko Neostar 3P 485W', label: 'Aiko Neostar 3P 485W' },
    { value: "Longi's 530W Hi-Mo6", label: "Longi's 530W Hi-Mo6" },
  ],
  inverters: {
    sigenergySinglePhase: [
      {
        value: 'Sigenergy Single Phase 3.6 kW',
        label: 'Single Phase Sigen Energy Controller 3.6 kW',
      },
      { value: 'Sigenergy Single Phase 4 kW', label: 'Single Phase Sigen Energy Controller 4 kW' },
      { value: 'Sigenergy Single Phase 5 kW', label: 'Single Phase Sigen Energy Controller 5 kW' },
      { value: 'Sigenergy Single Phase 6 kW', label: 'Single Phase Sigen Energy Controller 6 kW' },
      { value: 'Sigenergy Single Phase 8 kW', label: 'Single Phase Sigen Energy Controller 8 kW' },
      {
        value: 'Sigenergy Single Phase 10 kW',
        label: 'Single Phase Sigen Energy Controller 10 kW',
      },
      {
        value: 'Sigenergy Single Phase 12 kW',
        label: 'Single Phase Sigen Energy Controller 12 kW',
      },
    ],
    sigenergyThreePhase: [
      { value: 'Sigenergy Three Phase 6kW', label: 'Three Phase Sigen Energy Controller 6kW' },
      { value: 'Sigenergy Three Phase 8kW', label: 'Three Phase Sigen Energy Controller 8kW' },
      { value: 'Sigenergy Three Phase 10kW', label: 'Three Phase Sigen Energy Controller 10kW' },
      { value: 'Sigenergy Three Phase 15kW', label: 'Three Phase Sigen Energy Controller 15kW' },
      { value: 'Sigenergy Three Phase 20kW', label: 'Three Phase Sigen Energy Controller 20kW' },
      { value: 'Sigenergy Three Phase 25kW', label: 'Three Phase Sigen Energy Controller 25kW' },
    ],
    hanchuSinglePhase: [
      { value: 'Hanchu Single Phase 3.6 kW', label: 'Single Phase Hanchu Inverter 3.6 kW' },
      { value: 'Hanchu Single Phase 5 kW', label: 'Single Phase Hanchu Inverter 5 kW' },
      { value: 'Hanchu Single Phase 6 kW', label: 'Single Phase Hanchu Inverter 6 kW' },
    ],
  },
  batteries: {
    sigen: [
      { value: 'Sigen Battery 5kWh with LED', label: 'Sigen Battery 5kWh with LED' },
      { value: 'Sigen Battery 8kWh with LED', label: 'Sigen Battery 8kWh with LED' },
      { value: 'Sigen Battery 10 - 9.04kWh', label: 'Sigen Battery 10 - 9.04kWh' },
    ],
    hanchu: [
      { value: 'Hanchu Battery 5kWh', label: 'Hanchu Battery 5kWh' },
      { value: 'Hanchu Battery 9.4kWh', label: 'Hanchu Battery 9.4kWh' },
    ],
  },
};

// Installation API response schema (snake_case)
export const installationApiSchema = z.object({
  id: z.number(),
  customerId: z.number(),
  proposalId: z.number().nullable(),
  status: z.string(),
  systemType: z.string().optional(),
  designSystem: z.string().optional(),
  installationAddress: z.string().optional(),
  systemSize: z.number().optional(),
  panelCount: z.number().optional(),
  scheduledDate: z.string().nullable(),
  completedDate: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  // Customer object from API
  customer: z
    .object({
      id: z.number(),
      name: z.string().optional(),
      email: z.string().optional(),
      phone: z.string().optional(),
      address: z.string().optional(),
      postcode: z.string().optional(),
    })
    .optional(),
  // Legacy flat fields for backward compatibility
  customer_name: z.string().optional(),
  customer_address: z.string().optional(),
  customer_postcode: z.string().optional(),
  proposal_title: z.string().optional(),
  proposal_total_price: z.number().optional(),
  site_survey_id: z.number().nullable(),
  site_survey_status: z.string().nullable(),
  siteSurvey: z
    .object({
      id: z.number(),
      status: z.string(),
    })
    .optional(),
  // Legacy components array
  components: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        type: z.string(),
        quantity: z.number(),
      })
    )
    .optional(),
});

export type InstallationApiResponse = z.infer<typeof installationApiSchema>;

export const SITE_SURVEY_PHOTO_FIELDS = [
  'stringPlanFromOpenSolar',
  'aerialImageFromDrone',
  'generalImage1',
  'generalImage2',
  'generalImage3',
  'scaffoldPlanImage',
  'stringPlanImage',
  'generalStructureImage',
  'array1LocationImage',
  'array2LocationImage',
  'array3LocationImage',
  'potentialShadingImage',
  'photoMainsIncomer',
  'photoExistingDb',
  'photoProposedDcRoute',
  'photoNotes',
  'photoProposedArea',
  'photoMeasuringArea',
  'photoExteriorHome',
  'photoGateAccess',
  'photoFrontOfHome',
  'photoSidesOfHome',
  'rafterPhoto',
  'signsOfStructuralWorkPhoto',
  'structuralPhoto1',
  'structuralPhoto2',
  'structuralPhoto3',
  'structuralPhoto4',
  'photoIncomer',
  'photoMeter',
  'photoMainDb',
  'photoOther',
  'roofImage1',
  'roofImage2',
  'roofImage3',
  'membraneImage1',
  'membraneImage2',
  'cableRouteImage1',
  'cableRouteImage2',
  'proposedAreaPhoto',
  'dcRouteImage1',
  'dcRouteImage2',
  'dcRouteImage3',
  'earthPitLocationImage',
] as const;

export type SiteSurveyPhotoField = (typeof SITE_SURVEY_PHOTO_FIELDS)[number];

const SITE_SURVEY_PHOTO_FIELD_SET = new Set<string>(SITE_SURVEY_PHOTO_FIELDS);

export interface QueuedSiteSurveyPhoto {
  fieldPath: string;
  fieldName: string;
  dataUrl: string;
}

function isQueuedPhotoValue(value: unknown): value is string {
  return typeof value === 'string' && /^data:image\/[^;]+;base64,/i.test(value);
}

function isKnownPhotoField(fieldName: string): boolean {
  return (
    SITE_SURVEY_PHOTO_FIELD_SET.has(fieldName) ||
    /(?:photo|image)$/i.test(fieldName) ||
    /(?:photo|image)[A-Z0-9_]/.test(fieldName)
  );
}

function splitQueuedPhotoFields(
  value: unknown,
  path: string[] = [],
  queuedPhotos: QueuedSiteSurveyPhoto[] = []
): unknown {
  if (Array.isArray(value)) {
    return value.map((item, index) =>
      splitQueuedPhotoFields(item, [...path, String(index)], queuedPhotos)
    );
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  const cleanObject: Record<string, unknown> = {};

  Object.entries(value as Record<string, unknown>).forEach(([key, entryValue]) => {
    const nextPath = [...path, key];

    if (isKnownPhotoField(key) && isQueuedPhotoValue(entryValue)) {
      queuedPhotos.push({
        fieldPath: nextPath.join('.'),
        fieldName: key,
        dataUrl: entryValue,
      });
      return;
    }

    cleanObject[key] = splitQueuedPhotoFields(entryValue, nextPath, queuedPhotos);
  });

  return cleanObject;
}

export function splitSiteSurveyQueuedPhotos(data: Record<string, any>): {
  surveyData: Record<string, any>;
  queuedPhotos: QueuedSiteSurveyPhoto[];
} {
  const queuedPhotos: QueuedSiteSurveyPhoto[] = [];
  const surveyData = splitQueuedPhotoFields(data, [], queuedPhotos) as Record<string, any>;
  return { surveyData, queuedPhotos };
}

function summarizeForLog(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(summarizeForLog);
  }

  if (!value || typeof value !== 'object') {
    if (isQueuedPhotoValue(value)) {
      return `[queued image data URL: ${value.length} chars]`;
    }
    return value;
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, entryValue]) => [
      key,
      summarizeForLog(entryValue),
    ])
  );
}

// Installation UI model (camelCase)
export interface InstallationUI {
  id: number | string; // Can be string for customers without installations
  customerId: number;
  customer?: {
    id: number;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    postcode?: string;
  };
  installationAddress: string;
  systemSize: number;
  systemType?: string;
  panelCount?: number;
  panelQty?: number;
  inverterType?: string;
  batteryIncluded?: boolean;
  batteryType?: string;
  leadOwner: string;
  siteSurvey?: {
    id: number;
    status: string;
  };
  isCustomerOnly?: boolean; // Flag for customers without installations
  installationCount?: number; // Number of installations for this customer
  pipelineStageId?: number; // Pipeline stage ID
}

// Transform API response to UI model
export function transformInstallationApiToUI(apiData: InstallationApiResponse): InstallationUI {
  const rawData = apiData as InstallationApiResponse & Record<string, any>;
  const customerId =
    apiData.customerId ??
    rawData.customerid ??
    rawData.customer_id ??
    rawData.customer_id_real ??
    apiData.customer?.id ??
    apiData.id;

  const installation: InstallationUI = {
    id: apiData.id,
    customerId,
    installationAddress: apiData.installationAddress || '',
    systemSize: apiData.systemSize || 0,
    systemType: apiData.systemType,
    panelCount: apiData.panelCount,
    panelQty: apiData.panelCount, // Map panelCount to panelQty for compatibility
    leadOwner: '', // This field doesn't exist in API, will need to be populated elsewhere
  };

  // Transform customer data - handle both new object structure and legacy flat fields
  if (apiData.customer) {
    // New structure: customer object from API
    installation.customer = {
      id: apiData.customer.id || apiData.customerId,
      name: apiData.customer.name || '',
      email: apiData.customer.email,
      phone: apiData.customer.phone,
      address: apiData.customer.address,
      postcode: apiData.customer.postcode,
    };
  } else if (apiData.customer_name || apiData.customer_address) {
    // Legacy structure: flat fields
    installation.customer = {
      id: apiData.customerId,
      name: apiData.customer_name || '',
      address: apiData.customer_address,
      postcode: apiData.customer_postcode,
    };
  }

  // Transform site survey data - handle both new object structure and legacy flat fields
  if (apiData.siteSurvey) {
    // New structure: siteSurvey object from API
    installation.siteSurvey = {
      id: apiData.siteSurvey.id,
      status: apiData.siteSurvey.status,
    };
  } else if (apiData.site_survey_id) {
    // Legacy structure: flat fields
    installation.siteSurvey = {
      id: apiData.site_survey_id,
      status: apiData.site_survey_status || 'draft',
    };
  }

  // Note: Equipment details are not available in installations
  // They will be manually entered in site surveys or extracted from OpenSolar proposals

  // Add installation count and pipeline stage ID if present (for site survey mode)
  if ((apiData as any).installationCount !== undefined) {
    installation.installationCount = (apiData as any).installationCount;
  }
  if ((apiData as any).pipelineStageId !== undefined) {
    installation.pipelineStageId = (apiData as any).pipelineStageId;
  }
  if ((apiData as any).isCustomerOnly !== undefined) {
    installation.isCustomerOnly = (apiData as any).isCustomerOnly;
  }

  return installation;
}

// Extract autopopulation data from installation
export function extractAutopopulationData(installation: InstallationUI) {
  // Parse address to extract components
  const primaryAddress = installation.customer?.address || installation.installationAddress || '';
  const addressParts = parseUKAddress(primaryAddress, installation.installationAddress);
  const customerId = installation.customerId || installation.customer?.id;
  const installationId = installation.isCustomerOnly ? undefined : installation.id;

  const data = {
    customerId,
    installationId,

    // Client information
    clientName: installation.customer?.name || '',
    clientAddress: addressParts.streetAddress,
    clientCity: addressParts.city,
    clientPostCode: installation.customer?.postcode || addressParts.postcode || '',
    clientTelephone: installation.customer?.phone || '',
    clientEmail: installation.customer?.email || '',

    // System information
    systemType: installation.systemType || '',

    // Note: Equipment information (panels, inverters, batteries) should not be auto-populated
    // from installations. These will be filled manually or from OpenSolar proposals in the future.
    // The dropdown options are available for manual selection.

    // Auto-populate survey date with today's date
    surveyDate: new Date().toISOString().split('T')[0],

    // No default surveyor name - user must select from dropdown
    siteAssessorName: '',
  };

  return data;
}

// Helper function to parse UK addresses
function parseUKAddress(address: string, fallbackAddress?: string) {
  const UK_POSTCODE_REGEX = /[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}/i;
  let streetAddress = '';
  let city = '';
  let postcode = '';

  if (!address) return { streetAddress, city, postcode };

  // Try to extract postcode first
  const postcodeMatch = address.match(UK_POSTCODE_REGEX);
  if (postcodeMatch) {
    postcode = postcodeMatch[0].toUpperCase();
    // Remove postcode from address for further parsing
    address = address.replace(postcodeMatch[0], '').trim();
  }

  // Remove any trailing commas or extra spaces
  address = address.replace(/,\s*$/, '').replace(/\s+/g, ' ').trim();

  // Check if the address is now empty (was just a postcode)
  if (!address && postcode && fallbackAddress) {
    // Try to extract address from fallback (e.g., installation address)
    const fallbackMatch = fallbackAddress.match(UK_POSTCODE_REGEX);
    if (fallbackMatch) {
      address = fallbackAddress.replace(fallbackMatch[0], '').trim();
    } else {
      address = fallbackAddress;
    }
  }

  // Split by comma and clean up
  const lines = address
    .split(',')
    .map((line) => line.trim())
    .filter((line) => line);

  if (lines.length === 0) {
    streetAddress = address || '';
  } else if (lines.length === 1) {
    // If only one line, it's all street address
    streetAddress = lines[0];
  } else if (lines.length >= 2) {
    // Assume last line is city
    city = lines[lines.length - 1];
    streetAddress = lines.slice(0, -1).join(', ');
  }

  return { streetAddress, city, postcode };
}

// Transform form data for API submission
export function transformFormDataForApi(formData: Record<string, any>) {
  // The backend /save endpoint expects data in this structure:
  // { installationId, surveyData, photos, status }
  // Note: installationId is no longer used - surveys are linked to customers in Won stage

  console.log('[Transform] INPUT formData:', summarizeForLog(formData));
  console.log('[Transform] INPUT formData keys:', Object.keys(formData || {}));
  console.log('[Transform] INPUT customerId:', formData.customerId);

  // Extract installationId and status from formData (status is handled separately)
  const { installationId, status, ...rawSurveyFields } = formData;
  const { surveyData: surveyFields, queuedPhotos } = splitSiteSurveyQueuedPhotos(rawSurveyFields);

  console.log('[Transform] After destructuring:');
  console.log('[Transform] - installationId (extracted):', installationId);
  console.log('[Transform] - status (extracted):', status);
  console.log('[Transform] - surveyFields keys:', Object.keys(surveyFields));
  console.log('[Transform] - surveyFields.customerId:', surveyFields.customerId);
  console.log('[Transform] - surveyFields.highlightedFields:', surveyFields.highlightedFields);
  if (queuedPhotos.length > 0) {
    console.log(
      '[Transform] Stripped queued photo data URLs from save payload:',
      queuedPhotos.map((photo) => photo.fieldPath)
    );
  }

  // Return properly structured object for backend
  // installationId is always null since surveys are customer-based, not installation-based
  // Note: highlightedFields is now included in surveyFields and will be saved to DB
  const result = {
    installationId: null, // Always null - surveys are linked to customers, not installations
    surveyData: surveyFields,
    photos: [], // Photos are handled separately via photo upload endpoints
    status: status || 'draft',
  };

  console.log('[Transform] OUTPUT result:', summarizeForLog(result));
  console.log('[Transform] OUTPUT surveyData.customerId:', result.surveyData.customerId);

  return result;
}
